import * as yup from "yup";

const phoneRegExpEG_11 = /^(010|011|012|015)\d{8}$/;
const phoneRegExpEG_10 = /^(10|11|12|15)\d{8}$/;
const phoneRegExpUAE_9 = /^(?:50|51|52|54|55|56|58|2|3|4|6|7|9)\d{7}$/;
const emailRegExp = /^[\w.-]+@([\w-]+\.)+[\w-]{2,}$/;
const nationalIdRegex =
  /^[23]\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{5}\d{2}$/;

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const normalizeString = (v) => (typeof v === "string" ? v.trim() : v);

const fileSchema = yup
  .mixed()
  .test("fileSize", "should be less than 5MB.", (value) => {
    if (!value) return true;

    const file = Array.isArray(value) ? value[0] : value;
    if (typeof file === "string") return true; // URL case
    return !!file?.size && file.size <= MAX_FILE_SIZE;
  })
  .test(
    "fileType",
    "Invalid file type. Please upload the correct file type",
    (value) => {
      if (!value) return true;

      const file = Array.isArray(value) ? value[0] : value;
      if (typeof file === "string") return true; // URL case

      const type = file?.type || "";
      return type === "application/pdf" || type.startsWith("image/");
    },
  );

export const EnCampaignSchema = (type) =>
  yup.object().shape({
    name: yup
      .string()
      .transform(normalizeString)
      .min(3, "Username must be at least 3 characters long")
      .required("Required"),

    email: yup
      .string()
      .transform(normalizeString)
      .email("Please enter a valid email")
      .matches(emailRegExp, "Please enter a valid email")
      .required("Required"),

    mobilePhone: yup
      .string()
      .transform(normalizeString)
      .matches(
        phoneRegExpEG_11,
        "Phone number must start with (010/011/012/015) and be 11 digits",
      )
      .length(11, "Must be exactly 11 digits")
      .required("Required"),

    products:
      type === "exclusive-offers"
        ? yup.string().notRequired()
        : yup.string().required("Required"),
    preferredCallingTime:
      type === "catalogue"
        ? yup
            .string()
            .required("Required")
            .test("not-default", "Required", (v) => !!v && v !== "default")
        : yup.string().notRequired(),
  });

function buildMobileSchema({ flag, country }) {
  // Product flow has special handling for Egypt 10-digit format in your codebase
  if (flag === "product" && country === "Egypt") {
    return yup
      .string()
      .transform(normalizeString)
      .matches(
        phoneRegExpEG_10,
        "Phone number must start with (10/11/12/15) and be 10 digits",
      )
      .length(10, "Must be exactly 10 digits")
      .required("Required");
  }

  if (flag === "product" && country === "United Arab Emirates") {
    return yup
      .string()
      .transform(normalizeString)
      .matches(
        phoneRegExpUAE_9,
        "Phone number must start with (50/51/52/54/55/56/58) and be 9 digits",
      )
      .length(9, "Must be exactly 9 digits")
      .required("Required");
  }

  // Default Egypt 11-digit with leading 0
  return yup
    .string()
    .transform(normalizeString)
    .matches(
      phoneRegExpEG_11,
      "Phone number must start with (010/011/012/015) and be 11 digits",
    )
    .length(11, "Must be exactly 11 digits")
    .required("Required");
}

function buildBaseSchema({
  flag,
  country,
  requireGovernance = true,
  requirePreferredCallingTime = true,
}) {
  let schema = yup.object().shape({
    name: yup
      .string()
      .transform(normalizeString)
      .min(3, "Full Name must be at least 3 characters long")
      // Keep EN-only, but allow common separators
      .matches(
        /^[A-Za-z\s.'-]+$/,
        "Full Name can only contain letters and spaces",
      )
      .required("Full Name is required"),

    email: yup
      .string()
      .transform(normalizeString)
      .email("Please enter a valid email")
      .matches(emailRegExp, "Please enter a valid email")
      .required("Required"),

    // Optional: validate only if user entered a value
    nationalId: yup
      .string()
      .transform(normalizeString)
      .notRequired()
      .matches(nationalIdRegex, {
        message: "Please enter a valid National ID Number",
        excludeEmptyString: true,
      })
      .length(14, "Must be exactly 14 digits"),
    governance: yup.string().nullable().notRequired(),

    preferredCallingTime: requirePreferredCallingTime
      ? yup.string().required("Required")
      : yup.string().nullable().notRequired(),
  });

  schema = schema.shape({
    mobilePhone: buildMobileSchema({ flag, country }),
  });

  // UAE product attachments
  if (flag === "product" && country === "United Arab Emirates") {
    schema = schema.shape({
      passportUrl: fileSchema.required("Required"),
      workPermitUrl: fileSchema.notRequired(),
      hrLetter: fileSchema.notRequired(),
      bankStatementUrl: fileSchema.notRequired(),
      EgyptAddress: yup
        .string()
        .transform(normalizeString)
        .required("Required"),
    });
  }

  return schema;
}

/**
 * Simple in-memory cache to avoid rebuilding schemas frequently on rerenders.
 * Keyed by (flag,country,options).
 */
const schemaCache = new Map();
function getCachedSchema(key, builder) {
  if (schemaCache.has(key)) return schemaCache.get(key);
  const schema = builder();
  schemaCache.set(key, schema);
  return schema;
}

export const EnSchema = (flag, country) =>
  getCachedSchema(`EnSchema|${flag}|${country}`, () =>
    buildBaseSchema({ flag, country }),
  );

export const EnSchemaA = (flag, country) =>
  getCachedSchema(`EnSchemaA|${flag}|${country}`, () =>
    buildBaseSchema({ flag, country }),
  );

export const EnSchemaB = (flag, country) =>
  getCachedSchema(`EnSchemaB|${flag}|${country}`, () =>
    buildBaseSchema({ flag, country }).shape({
      products: yup.string().required("Required"),
    }),
  );

export const EnMortgageSchema = (flag, country) =>
  getCachedSchema(`EnMortgageSchema|${flag}|${country}`, () =>
    buildBaseSchema({ flag, country }),
  );

export const EnSchema_Application = (flag, country) =>
  getCachedSchema(`EnSchema_Application|${flag}|${country}`, () =>
    buildBaseSchema({
      flag,
      country,
      requireGovernance: false,
      requirePreferredCallingTime: false,
    }).shape({
      job: yup.string().required("Required"),
      resumeUrl: yup.string().url("Must be a valid URL").required("Required"),
      governorate: yup.string().required("Required"),
    }),
  );
function pickSchema(schema, fields) {
  const picked = {};
  fields.forEach((f) => {
    picked[f] = schema.fields[f];
  });
  return yup.object().shape(picked);
}
function buildOffersSchemaFromBase() {
  const base = buildBaseSchema({
    flag: "offers",
    country: "Egypt",
    requireGovernance: false,
    requirePreferredCallingTime: false,
  });

  return pickSchema(base, ["name", "email", "mobilePhone"]);
}
export const EnOffersSchema = () =>
  getCachedSchema("EnOffersSchema|EG|BASE", () => buildOffersSchemaFromBase());
